import { Controller } from "@hotwired/stimulus"

/**
 * Nested Checkboxes Controller
 *
 * Manages hierarchical checkbox behavior where parent checkboxes control child checkboxes.
 * Parent checkboxes are disabled on the server-side and only enabled/managed by JavaScript.
 *
 * Expected structure:
 * <div data-controller="nested-checkboxes">
 *   <input type="checkbox" data-nested-checkboxes-target="parent" data-parent-id="1" disabled />
 *   <input type="checkbox" data-nested-checkboxes-target="child" data-parent-id="1" />
 *   <input type="checkbox" data-nested-checkboxes-target="child" data-parent-id="1" />
 * </div>
 */
export default class extends Controller {
  static targets = ["allLabel", "child", "parent", "someLabel"]

  connect() {
    // Enable parent checkboxes (they're disabled by default for server-side rendering)
    this.parentTargets.forEach(parent => {
      parent.disabled = false
    })

    // Set initial state of parent checkboxes based on children
    this.updateAllParents()
    this.updateLabels()
  }

  // When a parent checkbox is clicked
  toggleParent(event) {
    const parent = event.target
    const parentId = parent.dataset.parentId
    const checked = parent.checked

    // Update all children with the same parent ID
    this.getChildrenForParent(parentId).forEach(child => {
      child.checked = checked
    })

    // Clear indeterminate state
    parent.indeterminate = false
    this.updateLabels()
  }

  // When a child checkbox is clicked
  toggleChild(event) {
    const child = event.target
    const parentId = child.dataset.parentId

    // Update the parent state based on all children
    this.updateParent(parentId)
    this.updateLabels()
  }

  // Update a specific parent's state based on its children
  updateParent(parentId) {
    const parent = this.getParent(parentId)
    if (!parent) return

    const children = this.getChildrenForParent(parentId)
    const checkedChildren = children.filter(c => c.checked)

    if (checkedChildren.length === 0) {
      // No children checked
      parent.checked = false
      parent.indeterminate = false
    } else if (checkedChildren.length === children.length) {
      // All children checked
      parent.checked = true
      parent.indeterminate = false
    } else {
      // Some children checked
      parent.checked = false
      parent.indeterminate = true
    }
  }

  // Update all parent checkboxes based on their children
  updateAllParents() {
    const parentIds = new Set(this.parentTargets.map(p => p.dataset.parentId))
    parentIds.forEach(parentId => this.updateParent(parentId))
  }

  // Get parent checkbox by parent ID
  getParent(parentId) {
    return this.parentTargets.find(p => p.dataset.parentId === parentId)
  }

  // Get all child checkboxes for a parent ID
  getChildrenForParent(parentId) {
    return this.childTargets.filter(c => c.dataset.parentId === parentId)
  }

  // Update All/Some label visibility based on checkbox state
  updateLabels() {
    if (!this.hasAllLabelTarget || !this.hasSomeLabelTarget) return

    const allChecked = this.childTargets.length > 0 &&
                       this.childTargets.every(child => child.checked)

    this.allLabelTarget.classList.toggle("hidden", !allChecked)
    this.someLabelTarget.classList.toggle("hidden", allChecked)
  }
}
